import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Divider,
    Group,
    Loader,
    NumberInput,
    ScrollArea,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { IconAlertTriangle, IconSearch } from '@tabler/icons-react';
import {
    getCatalogEntryByCandidateId,
    getCatalogEntryById,
    searchDirectory,
    type CatalogSearchField,
    type SearchDirectoryParams,
} from '../api/troubleshooting';
import type { CatalogEntry } from '../api/types';

const SEARCH_FIELD_OPTIONS: { value: CatalogSearchField; label: string }[] = [
    { value: 'trust_center_url', label: 'trust_center_url (vendor trust-center lookup)' },
    { value: 'domain', label: 'domain' },
    { value: 'website_url', label: 'website_url' },
    { value: 'open_trust_url', label: 'open_trust_url' },
    { value: 'name', label: 'name' },
    { value: 'searchable_key', label: 'searchable_key' },
];

/** Shared error alert for a failed lookup. */
const ErrorAlert = ({ error }: { error: unknown }): React.JSX.Element => (
    <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Lookup failed">
        {error instanceof Error ? error.message : 'Unknown error'}
    </Alert>
);

/** Pretty-printed raw JSON, so the exact wire shape is visible. */
const JsonBlock = ({ value }: { value: unknown }): React.JSX.Element => (
    <Code block style={{ maxHeight: 320, overflow: 'auto' }}>
        {JSON.stringify(value, null, 2)}
    </Code>
);

/**
 * Directory search card — reproduces the api handler's trust-center lookup.
 *
 * `results.data[0]` (highlighted) is what `VendorTrustCenterUrlHandler` would
 * link the vendor to: `vendorDirectoryId = data[0].id`, and
 * `catalogProductCandidateId = data[0].catalog_product_candidate_id`. Zero rows
 * means the handler resolves nothing and the vendor is left unlinked.
 */
const DirectorySearchCard = (): React.JSX.Element => {
    const [q, setQ] = useState('');
    const [by, setBy] = useState<CatalogSearchField>('trust_center_url');
    const [limit, setLimit] = useState<number>(10);
    const [args, setArgs] = useState<SearchDirectoryParams | null>(null);

    const query = useQuery({
        queryKey: ['ts-directory-search', args],
        queryFn: () => {
            if (!args) {
                throw new Error('No search arguments');
            }
            return searchDirectory(args);
        },
        enabled: args !== null,
    });

    const rows: CatalogEntry[] = query.data?.data ?? [];

    return (
        <Card withBorder padding="lg" radius="md" data-testid="TsDirectorySearchCard">
            <Stack gap="md">
                <div>
                    <Title order={4}>Directory search (trust-center lookup)</Title>
                    <Text size="sm" c="dimmed">
                        Runs <Code>GET /api/catalog?q=&amp;by=</Code> — the same call the api makes
                        to resolve a vendor&apos;s trust center URL to a catalog record. The first
                        result is what the vendor would be linked to.
                    </Text>
                </div>

                <Group align="flex-end" gap="sm" wrap="wrap">
                    <TextInput
                        label="Query (q)"
                        placeholder="https://compliance.zscaler.com?product=achievecompliance"
                        value={q}
                        onChange={(e) => {
                            setQ(e.currentTarget.value);
                        }}
                        style={{ flex: 1, minWidth: 320 }}
                        data-testid="TsSearchQueryInput"
                    />
                    <Select
                        label="Search by"
                        data={SEARCH_FIELD_OPTIONS}
                        value={by}
                        onChange={(value) => {
                            if (value) {
                                setBy(value as CatalogSearchField);
                            }
                        }}
                        allowDeselect={false}
                        w={280}
                        data-testid="TsSearchBySelect"
                    />
                    <NumberInput
                        label="Limit"
                        value={limit}
                        onChange={(value) => {
                            setLimit(typeof value === 'number' ? value : 10);
                        }}
                        min={1}
                        max={50}
                        w={100}
                    />
                    <Button
                        leftSection={<IconSearch size={16} />}
                        disabled={q.trim() === ''}
                        onClick={() => {
                            setArgs({ q: q.trim(), by, limit, includeStats: true });
                        }}
                        data-testid="TsSearchSubmit"
                    >
                        Search
                    </Button>
                </Group>

                {query.isFetching ? <Loader size="sm" /> : null}
                {query.error ? <ErrorAlert error={query.error} /> : null}

                {args !== null && !query.isFetching && !query.error ? (
                    <Stack gap="sm">
                        <Group gap="xs">
                            <Text fw={600}>
                                {rows.length} result{rows.length === 1 ? '' : 's'}
                            </Text>
                            {rows.length === 0 ? (
                                <Badge color="red" variant="light">
                                    No match — vendor would be left unlinked
                                </Badge>
                            ) : (
                                <Badge color="green" variant="light">
                                    Match found
                                </Badge>
                            )}
                        </Group>

                        {rows.length > 0 ? (
                            <ScrollArea>
                                <Table striped highlightOnHover withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>#</Table.Th>
                                            <Table.Th>id (vendorDirectoryId)</Table.Th>
                                            <Table.Th>candidate_id</Table.Th>
                                            <Table.Th>name</Table.Th>
                                            <Table.Th>product</Table.Th>
                                            <Table.Th>trust_center_url</Table.Th>
                                            <Table.Th>tenants</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {rows.map((row, index) => (
                                            <Table.Tr key={row.id}>
                                                <Table.Td>
                                                    {index === 0 ? (
                                                        <Badge size="sm" color="blue">
                                                            linked
                                                        </Badge>
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Code>{row.id}</Code>
                                                </Table.Td>
                                                <Table.Td>
                                                    {row.catalog_product_candidate_id}
                                                </Table.Td>
                                                <Table.Td>{row.name}</Table.Td>
                                                <Table.Td>{row.product_name ?? '—'}</Table.Td>
                                                <Table.Td>{row.trust_center_url ?? '—'}</Table.Td>
                                                <Table.Td>{row.tenant_count}</Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        ) : null}

                        <Divider label="Raw response" labelPosition="left" />
                        <JsonBlock value={query.data} />
                    </Stack>
                ) : null}
            </Stack>
        </Card>
    );
};

/** Look up a catalog entry by the compound id stored as `vendorDirectoryId`. */
const LookupByIdCard = (): React.JSX.Element => {
    const [id, setId] = useState('');
    const [submittedId, setSubmittedId] = useState<string | null>(null);

    const query = useQuery({
        queryKey: ['ts-catalog-by-id', submittedId],
        queryFn: () => {
            if (!submittedId) {
                throw new Error('No id');
            }
            return getCatalogEntryById(submittedId);
        },
        enabled: submittedId !== null,
        retry: false,
    });

    return (
        <Card withBorder padding="lg" radius="md" data-testid="TsLookupByIdCard">
            <Stack gap="md">
                <div>
                    <Title order={4}>Lookup by catalog id (vendorDirectoryId)</Title>
                    <Text size="sm" c="dimmed">
                        Runs <Code>GET /api/catalog/:id</Code> for a compound
                        <Code>provider:org:slug</Code> id.
                    </Text>
                </div>
                <Group align="flex-end" gap="sm">
                    <TextInput
                        label="vendorDirectoryId"
                        placeholder="MANUAL:788ff152-3e01-4dab-ada1-8348edc673a8:default"
                        value={id}
                        onChange={(e) => {
                            setId(e.currentTarget.value);
                        }}
                        style={{ flex: 1, minWidth: 360 }}
                    />
                    <Button
                        variant="light"
                        disabled={id.trim() === ''}
                        onClick={() => {
                            setSubmittedId(id.trim());
                        }}
                    >
                        Fetch
                    </Button>
                </Group>
                {query.isFetching ? <Loader size="sm" /> : null}
                {query.error ? <ErrorAlert error={query.error} /> : null}
                {query.data ? <JsonBlock value={query.data} /> : null}
            </Stack>
        </Card>
    );
};

/** Look up a catalog entry by the numeric `catalogProductCandidateId`. */
const LookupByCandidateIdCard = (): React.JSX.Element => {
    const [candidateId, setCandidateId] = useState<number | ''>('');
    const [submittedId, setSubmittedId] = useState<number | null>(null);

    const query = useQuery({
        queryKey: ['ts-catalog-by-candidate', submittedId],
        queryFn: () => {
            if (submittedId === null) {
                throw new Error('No candidate id');
            }
            return getCatalogEntryByCandidateId(submittedId);
        },
        enabled: submittedId !== null,
        retry: false,
    });

    return (
        <Card withBorder padding="lg" radius="md" data-testid="TsLookupByCandidateIdCard">
            <Stack gap="md">
                <div>
                    <Title order={4}>Lookup by candidate id (catalogProductCandidateId)</Title>
                    <Text size="sm" c="dimmed">
                        Runs <Code>GET /api/catalog/by-candidate/:id</Code>.
                    </Text>
                </div>
                <Group align="flex-end" gap="sm">
                    <NumberInput
                        label="catalogProductCandidateId"
                        placeholder="12345"
                        value={candidateId}
                        onChange={(value) => {
                            setCandidateId(typeof value === 'number' ? value : '');
                        }}
                        min={1}
                        w={260}
                    />
                    <Button
                        variant="light"
                        disabled={candidateId === ''}
                        onClick={() => {
                            if (typeof candidateId === 'number') {
                                setSubmittedId(candidateId);
                            }
                        }}
                    >
                        Fetch
                    </Button>
                </Group>
                {query.isFetching ? <Loader size="sm" /> : null}
                {query.error ? <ErrorAlert error={query.error} /> : null}
                {query.data ? <JsonBlock value={query.data} /> : null}
            </Stack>
        </Card>
    );
};

/**
 * Troubleshooting view: ad-hoc VGS directory lookups for diagnosing why a
 * vendor's trust-center URL does or doesn't resolve to a catalog record
 * (`vendorDirectoryId` / `catalogProductCandidateId` resolution). Rendered at
 * `/troubleshooting`.
 */
export const TroubleshootingView = (): React.JSX.Element => (
    <Stack gap="xl" flex={1} mih={0} data-testid="TroubleshootingView">
        <div>
            <Title order={2}>Troubleshooting</Title>
            <Text size="sm" c="dimmed">
                Directly exercise the VGS directory find methods that back vendor trust-center
                resolution. Use these to confirm whether a given trust center URL matches a catalog
                record before it reaches a tenant.
            </Text>
        </div>
        <ScrollArea flex={1} mih={0}>
            <Stack gap="xl">
                <DirectorySearchCard />
                <LookupByIdCard />
                <LookupByCandidateIdCard />
            </Stack>
        </ScrollArea>
    </Stack>
);
